import { useEffect, useMemo, useRef, useState } from 'react';
import { Emphasis, RichText, parseEmphasis } from './RichText';

/**
 * LLM 텍스트가 "타이핑"이 아니라 **만들어지듯** 나타나는 효과.
 * - 단어(어절) 단위로 공개하고, 각 어절은 금빛 안개(블러+글로우)에서
 *   서서히 또렷해지는 CSS 애니메이션(.magic-word)으로 떠오름
 * - 공개 속도는 시간 기반 + 밀린 양 비례 → SSE가 뚝뚝 와도 일정하고 부드럽게
 * - 캐럿(타이핑 커서) 없음 — 대신 생성 중일 때 꼬리에 은은한 ✦ 반짝임
 */

const BASE_WORDS_PER_SEC = 4.5; // 기본 공개 속도 (어절/초)
const CATCHUP_FACTOR = 0.35; // 밀린 어절 1개당 추가 속도(초당) — 클수록 빨리 따라잡음
const MAX_WORDS_PER_SEC = 22;
/** 한 틱에 반영할 최대 경과 시간(초) — 오래 멈췄다 돌아와도 한꺼번에 쏟아지지 않게 */
const MAX_TICK_SEC = 0.1;
/**
 * rAF가 이 시간 넘게 오지 않으면 타이머가 대신 공개를 이어간다.
 *
 * ⚠️ rAF만 믿으면 안 된다. 토스 미니앱에서 전면 광고가 닫힌 직후 웹뷰가 rAF를 다시 주지 않아,
 * 해석은 끝까지 받아 놓고도 화면이 첫 어절에서 멈춘 채 끝나지 않았다 (2026-10-10 실기기 제보).
 * 스트림 완료 뒤에도 전부 드러나기 전에는 완성본으로 바뀌지 않으므로, 진행이 멈추면 영영 멈춘다.
 */
const FALLBACK_TICK_MS = 100;

interface Token {
  text: string;
  bold: boolean;
}

export function MagicText({
  text,
  active = false,
  onComplete,
}: {
  text: string;
  active?: boolean;
  /**
   * 마지막 어절까지 화면에 드러난 뒤 호출.
   * 스트림이 끝나도(active=false) 밀린 어절을 계속 펼치는 중이라,
   * "생성 완료"와 "다 보이는 상태"는 다르다. 결과를 이미지로 굽는 것처럼
   * 본문이 온전해야 하는 동작은 이 신호를 기다려야 한다.
   */
  onComplete?: () => void;
}) {
  /**
   * 어절 단위 토큰 — 앞뒤 공백을 어절에 붙여 문단 사이 빈 줄이 사라지지 않게 한다.
   * 강조(**…**) 구간을 먼저 나눈 뒤 각 구간 안에서 어절을 쪼개, 공개되는 순간부터
   * 굵기가 정해져 있게 한다 (나중에 굵어지면 글자가 다시 배치되며 흔들린다).
   */
  const tokens = useMemo<Token[]>(() => {
    const out: Token[] = [];
    for (const seg of parseEmphasis(text)) {
      const words = seg.text.match(/\s*\S+\s*/g);
      if (!words) {
        // 공백만 있는 구간 — 앞 어절에 붙여 여백을 지킨다
        if (out.length) out[out.length - 1]!.text += seg.text;
        else out.push({ text: seg.text, bold: seg.bold });
        continue;
      }
      for (const w of words) out.push({ text: w, bold: seg.bold });
    }
    return out;
  }, [text]);
  const [shown, setShown] = useState(0);
  const acc = useRef(0);
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;

  // 새 리딩 시작(텍스트 리셋) → 처음부터
  useEffect(() => {
    if (tokens.length < shown) {
      setShown(0);
      acc.current = 0;
    }
  }, [tokens.length, shown]);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    // 경과 시간 기준이라 rAF와 타이머 어느 쪽이 불러도 속도가 같다
    const advance = () => {
      const now = performance.now();
      const dt = Math.min(MAX_TICK_SEC, (now - last) / 1000);
      last = now;
      setShown((s) => {
        const total = tokensRef.current.length;
        if (s >= total) return s;
        const backlog = total - s;
        const rate = Math.min(MAX_WORDS_PER_SEC, BASE_WORDS_PER_SEC + backlog * CATCHUP_FACTOR);
        acc.current += rate * dt;
        const step = Math.floor(acc.current);
        if (step <= 0) return s;
        acc.current -= step;
        return Math.min(total, s + step);
      });
    };
    const frame = () => {
      advance();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    // rAF가 제때 오는 동안에는 아무것도 하지 않는다 — 멈췄을 때만 대신 민다.
    // 기준을 반 주기로 두는 이유: 타이머가 주기보다 살짝 일찍 깨면 한 틱을 통째로 건너뛰어 반속이 된다
    const fallback = setInterval(() => {
      if (performance.now() - last >= FALLBACK_TICK_MS / 2) advance();
    }, FALLBACK_TICK_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(fallback);
    };
  }, []);

  const generating = active || shown < tokens.length;

  // 매 렌더마다 새 함수가 와도 effect가 다시 돌지 않도록 ref로 받는다
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const revealed = !generating && tokens.length > 0;
  useEffect(() => {
    if (revealed) onCompleteRef.current?.();
  }, [revealed]);

  if (!generating) return <RichText text={text} />;

  return (
    <>
      {tokens.slice(0, shown).map((tok, i) =>
        tok.bold ? (
          <Emphasis key={i}>
            <span className="magic-word">{tok.text}</span>
          </Emphasis>
        ) : (
          <span key={i} className="magic-word">
            {tok.text}
          </span>
        ),
      )}
      <span className="magic-spark" aria-hidden>
        ✦
      </span>
    </>
  );
}
